import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { User, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-surface border border-line rounded-xl shadow-xs overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-line">
        <Icon className="w-4 h-4 text-faint" />
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
      </div>
      <div className="px-5 py-5">{children}</div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <p className="text-xs font-medium text-muted mb-1">{label}</p>
      <p className={cn('text-sm text-ink', mono && 'font-mono')}>{value || '—'}</p>
    </div>
  );
}

const roleLabels = { student: 'Estudiante', teacher: 'Profesor', simulacion: 'Simulación' };

const inputClass = 'w-full h-10 px-3 bg-sunken border border-line rounded-lg text-sm text-ink placeholder:text-faint focus:outline-none focus:border-accent focus:bg-surface focus:shadow-focus transition-all';

export default function Settings() {
  const { user } = useAuth();

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwStatus, setPwStatus] = useState(null);
  const [pwLoading, setPwLoading] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setPwStatus({ type: 'error', msg: 'Las contraseñas nuevas no coinciden.' });
      return;
    }
    if (newPassword.length < 6) {
      setPwStatus({ type: 'error', msg: 'La nueva contraseña debe tener al menos 6 caracteres.' });
      return;
    }
    setPwLoading(true);
    setPwStatus(null);
    try {
      await api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword });
      setPwStatus({ type: 'success', msg: 'Contraseña cambiada exitosamente.' });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al cambiar la contraseña.';
      setPwStatus({ type: 'error', msg });
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="min-h-full px-6 py-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Configuración</h1>
          <p className="text-sm text-muted mt-1">Gestiona tu cuenta y tus credenciales.</p>
        </div>

        <Section title="Perfil de usuario" icon={User}>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-accent-soft flex items-center justify-center flex-shrink-0">
              <span className="text-lg font-semibold text-accent-ink">
                {user?.name?.charAt(0)?.toUpperCase() || user?.student_number?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div>
              <p className="text-sm font-semibold text-ink">{user?.name || user?.student_number}</p>
              <p className="text-xs text-muted">{roleLabels[user?.role] || user?.role}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nombre" value={user?.name} />
            <Field label="Número de estudiante" value={user?.student_number} mono />
            <Field label="Correo electrónico" value={user?.email} />
            <Field label="Rol" value={roleLabels[user?.role] || user?.role} />
          </div>
        </Section>

        <Section title="Cambiar contraseña" icon={Lock}>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Contraseña actual</label>
              <input type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} required className={inputClass} placeholder="••••••••" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Nueva contraseña</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6} className={inputClass} placeholder="Mínimo 6 caracteres" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Confirmar nueva contraseña</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className={inputClass} placeholder="••••••••" />
            </div>

            {pwStatus && (
              <div className={cn(
                'flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm',
                pwStatus.type === 'success' ? 'bg-success-soft text-success-ink' : 'bg-danger-soft text-danger-ink'
              )}>
                {pwStatus.type === 'success'
                  ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                {pwStatus.msg}
              </div>
            )}

            <button
              type="submit"
              disabled={pwLoading}
              className="h-10 px-4 bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-colors"
            >
              {pwLoading ? 'Guardando…' : 'Actualizar contraseña'}
            </button>
          </form>
        </Section>
      </div>
    </div>
  );
}
